import { mm, type Um } from './units.js';
import type { Issue } from './model.js';

/**
 * Room ("Prostorija"): a floor plan drawn as lines between shared points. Walls get a height and a
 * thickness (outward, away from the room) and become 3D walls; guides ("vodilje") are construction
 * lines only — they help to place points and are never built. Openings (windows, doors) cut the walls,
 * installations (sockets, drain, water supply) sit on the inner face.
 *
 * Plan coordinates are the room's world X (right) and Z (towards the front / the viewer), in µm. In
 * the plan view +Z is drawn downwards, so visual angles are measured counter-clockwise on screen:
 * `atan2(-dz, dx)`.
 */

export interface RoomPoint {
  id: string;
  x: Um;
  z: Um;
  /** Height of the wall corner (vertical edge) here; absent = the wall height. Different heights slope the wall top. */
  h?: Um;
}

export type RoomLineKind = 'wall' | 'guide';

export interface RoomLine {
  id: string;
  a: string;
  b: string;
  kind: RoomLineKind;
  /** Wall built to the other side: the inner face (the room side) is swapped with the outer one. */
  flip?: boolean;
}

export type RoomItemKind = 'window' | 'door' | 'socket' | 'drain' | 'water';

export interface RoomItem {
  id: string;
  kind: RoomItemKind;
  lineId: string;
  /** The wall end the offset is measured from (its start `a` or its end `b`). */
  from: 'a' | 'b';
  /** Distance of the item's near edge from that wall end, along the wall. */
  offset: Um;
  width: Um;
  height: Um;
  /** Height of the item's bottom edge above the floor (window sill, socket height). */
  elevation: Um;
}

export interface RoomPlan {
  points: RoomPoint[];
  lines: RoomLine[];
  items: RoomItem[];
  /** Wall height; null while only the plan is drawn (no 3D walls yet). */
  wallHeight: Um | null;
  wallThickness: Um;
  /** Draw the ceiling (over a closed room). */
  showCeiling?: boolean;
}

export const ROOM_ITEM_LABEL: Record<RoomItemKind, string> = { window: 'Prozor', door: 'Vrata', socket: 'Utičnica', drain: 'Odvod', water: 'Dovod vode' };
/** Openings cut the wall; the other items are installations on its inner face. */
export const ROOM_OPENINGS: RoomItemKind[] = ['window', 'door'];
export const ROOM_ITEM_DEFAULTS: Record<RoomItemKind, { width: Um; height: Um; elevation: Um }> = {
  window: { width: mm(1200), height: mm(1400), elevation: mm(900) },
  door: { width: mm(900), height: mm(2050), elevation: 0 },
  socket: { width: mm(80), height: mm(80), elevation: mm(300) },
  drain: { width: mm(50), height: mm(50), elevation: mm(500) },
  water: { width: mm(20), height: mm(20), elevation: mm(500) },
};
export const DEFAULT_WALL_HEIGHT = mm(2600);
export const DEFAULT_WALL_THICKNESS = mm(100);
const SAME_POINT = 500; // 0.5 mm

/** Changes the height of one wall corner (vertical edge); `null` = back to the wall height. */
export function setCornerHeight(plan: RoomPlan, pointId: string, h: Um | null): boolean {
  const p = plan.points.find((x) => x.id === pointId);
  if (!p) return false;
  if (h === null || h === plan.wallHeight) delete p.h;
  else p.h = h;
  return true;
}

export function emptyRoomPlan(): RoomPlan {
  return { points: [], lines: [], items: [], wallHeight: null, wallThickness: DEFAULT_WALL_THICKNESS };
}

export function nextRoomId(plan: RoomPlan, prefix: 'p' | 'l' | 'i'): string {
  const used = new Set([...plan.points.map((p) => p.id), ...plan.lines.map((l) => l.id), ...plan.items.map((i) => i.id)]);
  let n = (prefix === 'p' ? plan.points : prefix === 'l' ? plan.lines : plan.items).length + 1;
  while (used.has(`${prefix}${n}`)) n++;
  return `${prefix}${n}`;
}

export const roomPoint = (plan: RoomPlan, id: string): RoomPoint | undefined => plan.points.find((p) => p.id === id);

export interface LineGeom {
  a: RoomPoint;
  b: RoomPoint;
  dx: number;
  dz: number;
  /** Length (µm). */
  length: number;
  /** Unit direction a → b. */
  ux: number;
  uz: number;
}

export function lineGeom(plan: RoomPlan, line: RoomLine): LineGeom | null {
  const a = roomPoint(plan, line.a);
  const b = roomPoint(plan, line.b);
  if (!a || !b) return null;
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const length = Math.hypot(dx, dz);
  return { a, b, dx, dz, length, ux: length ? dx / length : 1, uz: length ? dz / length : 0 };
}

/** Visual angle (degrees, counter-clockwise on screen, 0 = to the right) of a direction. */
export const visualAngle = (dx: number, dz: number): number => {
  const d = (Math.atan2(-dz, dx) * 180) / Math.PI;
  return d < 0 ? d + 360 : d;
};
/** Point at visual angle `deg` and distance `len` from (x, z). */
export function polar(x: number, z: number, deg: number, len: number): { x: Um; z: Um } {
  const r = (deg * Math.PI) / 180;
  return { x: Math.round(x + Math.cos(r) * len), z: Math.round(z - Math.sin(r) * len) };
}

/** The point two lines share (null = they do not meet). */
export function sharedPoint(l: RoomLine, m: RoomLine): string | null {
  return l.a === m.a || l.a === m.b ? l.a : l.b === m.a || l.b === m.b ? l.b : null;
}

/** Lines that meet `line` at its ends. */
export function connectedLines(plan: RoomPlan, line: RoomLine): RoomLine[] {
  return plan.lines.filter((m) => m !== line && sharedPoint(line, m));
}

/** Inner angle (0 … 180°) between two lines at the point they share, or null when they do not meet. */
export function cornerAngle(plan: RoomPlan, lineId: string, refId: string): number | null {
  const l = plan.lines.find((x) => x.id === lineId);
  const r = plan.lines.find((x) => x.id === refId);
  if (!l || !r) return null;
  const p = sharedPoint(l, r);
  if (!p) return null;
  const P = roomPoint(plan, p)!;
  const A = roomPoint(plan, r.a === p ? r.b : r.a)!;
  const B = roomPoint(plan, l.a === p ? l.b : l.a)!;
  const a1 = Math.atan2(A.z - P.z, A.x - P.x);
  const a2 = Math.atan2(B.z - P.z, B.x - P.x);
  let d = Math.abs(a1 - a2) * (180 / Math.PI);
  if (d > 180) d = 360 - d;
  return d;
}

/** Changes a line's length; the other end (`fixed`) stays. Lines sharing the moved point follow it. */
export function setLineLength(plan: RoomPlan, lineId: string, length: Um, fixed: 'a' | 'b' = 'a'): boolean {
  const l = plan.lines.find((x) => x.id === lineId);
  const g = l && lineGeom(plan, l);
  if (!l || !g || length <= 0) return false;
  const [from, to, sx, sz] = fixed === 'a' ? [g.a, g.b, g.ux, g.uz] : [g.b, g.a, -g.ux, -g.uz];
  to.x = Math.round(from.x + sx * length);
  to.z = Math.round(from.z + sz * length);
  return true;
}

/** Rotates a line about its end `pivot` so its visual angle is `deg`; its length stays. */
export function setLineDirection(plan: RoomPlan, lineId: string, deg: number, pivot: 'a' | 'b' = 'a'): boolean {
  const l = plan.lines.find((x) => x.id === lineId);
  const g = l && lineGeom(plan, l);
  if (!l || !g) return false;
  const [from, to] = pivot === 'a' ? [g.a, g.b] : [g.b, g.a];
  const p = polar(from.x, from.z, deg, g.length);
  to.x = p.x;
  to.z = p.z;
  return true;
}

/**
 * Sets the inner angle between `lineId` and `refId` at their shared point: the far end of `lineId`
 * swings around that point (length kept), staying on the side of `refId` it was on.
 */
export function setCornerAngle(plan: RoomPlan, lineId: string, refId: string, deg: number): boolean {
  const l = plan.lines.find((x) => x.id === lineId);
  const r = plan.lines.find((x) => x.id === refId);
  if (!l || !r || deg <= 0 || deg >= 360) return false;
  const p = sharedPoint(l, r);
  if (!p) return false;
  const P = roomPoint(plan, p)!;
  const A = roomPoint(plan, r.a === p ? r.b : r.a)!;
  const B = roomPoint(plan, l.a === p ? l.b : l.a)!;
  const refAngle = visualAngle(A.x - P.x, A.z - P.z);
  const cur = visualAngle(B.x - P.x, B.z - P.z);
  // Which side of the reference ray the line is on (counter-clockwise or clockwise from it).
  const ccw = ((cur - refAngle + 360) % 360) <= 180;
  const len = Math.hypot(B.x - P.x, B.z - P.z);
  const q = polar(P.x, P.z, refAngle + (ccw ? deg : -deg), len);
  B.x = q.x;
  B.z = q.z;
  return true;
}

/** An existing point at these coordinates, or a new one. */
function pointFor(plan: RoomPlan, at: { id?: string; x: Um; z: Um }): RoomPoint {
  const byId = at.id ? roomPoint(plan, at.id) : undefined;
  if (byId) return byId;
  const near = plan.points.find((p) => Math.abs(p.x - at.x) <= SAME_POINT && Math.abs(p.z - at.z) <= SAME_POINT);
  if (near) return near;
  const p: RoomPoint = { id: nextRoomId(plan, 'p'), x: Math.round(at.x), z: Math.round(at.z) };
  plan.points.push(p);
  return p;
}

/** Adds a line between two points (existing ones are reused); null for a zero-length line. */
export function addRoomLine(plan: RoomPlan, from: { id?: string; x: Um; z: Um }, to: { id?: string; x: Um; z: Um }, kind: RoomLineKind): RoomLine | null {
  if (Math.hypot(to.x - from.x, to.z - from.z) <= SAME_POINT) return null;
  const a = pointFor(plan, from);
  const b = pointFor(plan, to);
  if (a === b) return null;
  const line: RoomLine = { id: nextRoomId(plan, 'l'), a: a.id, b: b.id, kind };
  plan.lines.push(line);
  return line;
}

/** Removes a line, its items and the points no other line uses. */
export function removeRoomLine(plan: RoomPlan, lineId: string): void {
  plan.lines = plan.lines.filter((l) => l.id !== lineId);
  plan.items = plan.items.filter((i) => i.lineId !== lineId);
  const used = new Set(plan.lines.flatMap((l) => [l.a, l.b]));
  plan.points = plan.points.filter((p) => used.has(p.id));
}

/** Along-the-wall span of an item (µm from the wall start `a`). */
export function itemSpan(plan: RoomPlan, item: RoomItem): { u0: number; u1: number } | null {
  const l = plan.lines.find((x) => x.id === item.lineId);
  const g = l && lineGeom(plan, l);
  if (!g) return null;
  const u0 = item.from === 'a' ? item.offset : g.length - item.offset - item.width;
  return { u0, u1: u0 + item.width };
}

export function addRoomItem(plan: RoomPlan, kind: RoomItemKind, lineId: string, centreU: number): RoomItem | null {
  const l = plan.lines.find((x) => x.id === lineId);
  const g = l && lineGeom(plan, l);
  if (!l || !g || l.kind !== 'wall') return null;
  const d = ROOM_ITEM_DEFAULTS[kind];
  const width = Math.min(d.width, Math.round(g.length));
  const offset = Math.max(0, Math.min(Math.round(centreU - width / 2), Math.round(g.length - width)));
  const item: RoomItem = { id: nextRoomId(plan, 'i'), kind, lineId, from: 'a', offset, width, height: d.height, elevation: d.elevation };
  plan.items.push(item);
  return item;
}

/**
 * Centre (µm from the wall start) for a new item of the given width: the middle of the widest free
 * stretch between the items already on the wall (the wall middle when it is empty).
 */
export function freeItemCentre(plan: RoomPlan, lineId: string, width: number): number {
  const l = plan.lines.find((x) => x.id === lineId);
  const g = l && lineGeom(plan, l);
  if (!g) return 0;
  const busy = plan.items
    .filter((i) => i.lineId === lineId)
    .map((i) => itemSpan(plan, i)!)
    .sort((a, b) => a.u0 - b.u0);
  const gaps: [number, number][] = [];
  let cur = 0;
  for (const b of busy) {
    if (b.u0 > cur) gaps.push([cur, b.u0]);
    cur = Math.max(cur, b.u1);
  }
  if (cur < g.length) gaps.push([cur, g.length]);
  const fits = gaps.filter(([a, b]) => b - a >= width);
  const pool = fits.length ? fits : gaps;
  if (!pool.length) return g.length / 2;
  const best = pool.reduce((p, q) => (q[1] - q[0] > p[1] - p[0] ? q : p));
  return (best[0] + best[1]) / 2;
}

/** Swaps the inner and the outer side of a wall (its thickness goes to the other side of the line). */
export function flipRoomWall(plan: RoomPlan, lineId: string): void {
  const l = plan.lines.find((x) => x.id === lineId);
  if (!l) return;
  if (l.flip) delete l.flip;
  else l.flip = true;
}

/** Measures the item from the other wall end without moving it. */
export function flipItemReference(plan: RoomPlan, itemId: string): void {
  const it = plan.items.find((i) => i.id === itemId);
  const l = it && plan.lines.find((x) => x.id === it.lineId);
  const g = l && lineGeom(plan, l);
  if (!it || !g) return;
  it.offset = Math.round(g.length - it.offset - it.width);
  it.from = it.from === 'a' ? 'b' : 'a';
}

// ---------- snapping and drawing ----------

export interface RoomSnapOptions {
  /** Capture distance (µm; the UI passes a few pixels). */
  tolerance: number;
  points: boolean;
  /** Onto lines (guides as endless lines) and their intersections. */
  lines: boolean;
  /** Grid step (µm), null = off. */
  grid: number | null;
  /** Points / lines left out (e.g. the point being dragged). */
  ignorePoint?: string;
}

export type RoomSnapKind = 'point' | 'intersection' | 'line' | 'grid';
export interface RoomSnap {
  x: number;
  z: number;
  kind: RoomSnapKind | null;
  pointId?: string;
}

/** Infinite line (guides) or segment (walls) through a line's points. */
function lineSeg(plan: RoomPlan, l: RoomLine) {
  const g = lineGeom(plan, l);
  return g && { g, endless: l.kind === 'guide' };
}

function intersect(p: { g: LineGeom; endless: boolean }, q: { g: LineGeom; endless: boolean }): { x: number; z: number } | null {
  const den = p.g.dx * q.g.dz - p.g.dz * q.g.dx;
  if (Math.abs(den) < 1e-9 * p.g.length * q.g.length) return null;
  const t = ((q.g.a.x - p.g.a.x) * q.g.dz - (q.g.a.z - p.g.a.z) * q.g.dx) / den;
  const s = ((q.g.a.x - p.g.a.x) * p.g.dz - (q.g.a.z - p.g.a.z) * p.g.dx) / den;
  const inside = (v: number, endless: boolean) => endless || (v >= -1e-6 && v <= 1 + 1e-6);
  if (!inside(t, p.endless) || !inside(s, q.endless)) return null;
  return { x: p.g.a.x + t * p.g.dx, z: p.g.a.z + t * p.g.dz };
}

function project(seg: { g: LineGeom; endless: boolean }, x: number, z: number): { x: number; z: number; d: number } {
  const { g } = seg;
  let t = ((x - g.a.x) * g.dx + (z - g.a.z) * g.dz) / (g.length * g.length || 1);
  if (!seg.endless) t = Math.max(0, Math.min(1, t));
  const px = g.a.x + t * g.dx;
  const pz = g.a.z + t * g.dz;
  return { x: px, z: pz, d: Math.hypot(px - x, pz - z) };
}

/** Snaps a plan point: existing points, then line intersections, then onto a line, then the grid. */
export function snapRoomPoint(plan: RoomPlan, x: number, z: number, o: RoomSnapOptions): RoomSnap {
  if (o.points) {
    let best: { p: RoomPoint; d: number } | null = null;
    for (const p of plan.points) {
      if (p.id === o.ignorePoint) continue;
      const d = Math.hypot(p.x - x, p.z - z);
      if (d <= o.tolerance && (!best || d < best.d)) best = { p, d };
    }
    if (best) return { x: best.p.x, z: best.p.z, kind: 'point', pointId: best.p.id };
    // The origin of the room (where the furniture's 0 is) is a point too.
    if (Math.hypot(x, z) <= o.tolerance) return { x: 0, z: 0, kind: 'point' };
  }
  if (o.lines) {
    const segs = plan.lines.filter((l) => l.a !== o.ignorePoint && l.b !== o.ignorePoint).map((l) => lineSeg(plan, l)).filter((s): s is NonNullable<typeof s> => !!s && s.g.length > 0);
    let best: { x: number; z: number; d: number } | null = null;
    for (let i = 0; i < segs.length; i++)
      for (let j = i + 1; j < segs.length; j++) {
        const c = intersect(segs[i]!, segs[j]!);
        const d = c ? Math.hypot(c.x - x, c.z - z) : Infinity;
        if (c && d <= o.tolerance && (!best || d < best.d)) best = { ...c, d };
      }
    if (best) return { x: Math.round(best.x), z: Math.round(best.z), kind: 'intersection' };
    let near: { x: number; z: number; d: number } | null = null;
    for (const s of segs) {
      const p = project(s, x, z);
      if (p.d <= o.tolerance && (!near || p.d < near.d)) near = p;
    }
    if (near) return { x: Math.round(near.x), z: Math.round(near.z), kind: 'line' };
  }
  if (o.grid) return { x: Math.round(x / o.grid) * o.grid, z: Math.round(z / o.grid) * o.grid, kind: 'grid' };
  return { x: Math.round(x), z: Math.round(z), kind: null };
}

export interface RoomDrawInput {
  /** Start of the new line. */
  start: { x: number; z: number };
  /** Cursor (plan µm). */
  cursor: { x: number; z: number };
  /** Line drawn just before (its end is `start`): typed angles are measured from it. */
  prevLineId: string | null;
  /** Typed length (µm) / angle (degrees); null = from the cursor. */
  length: number | null;
  angle: number | null;
  /** Increments (null = off): length step (µm), angle step (degrees). */
  lengthStep: number | null;
  angleStep: number | null;
  snap: RoomSnapOptions | null;
}

/**
 * End point of the line being drawn. Typed values win; then a point / line snap near the cursor;
 * otherwise the cursor direction and distance, rounded to the increments. Angles are the inner angle
 * to the previous line (turning to the side of the cursor), or the visual angle when there is none.
 */
export function roomDrawTarget(plan: RoomPlan, d: RoomDrawInput): RoomSnap & { length: number; angle: number } {
  const { start, cursor } = d;
  const prev = d.prevLineId ? plan.lines.find((l) => l.id === d.prevLineId) : undefined;
  const pg = prev && lineGeom(plan, prev);
  // Reference ray: from the start back along the previous line.
  let ref: number | null = null;
  if (pg) {
    const startIsB = Math.hypot(pg.b.x - start.x, pg.b.z - start.z) <= Math.hypot(pg.a.x - start.x, pg.a.z - start.z);
    const other = startIsB ? pg.a : pg.b;
    ref = visualAngle(other.x - start.x, other.z - start.z);
  }
  const curAngle = visualAngle(cursor.x - start.x, cursor.z - start.z);
  const ccw = ref === null ? true : ((curAngle - ref + 360) % 360) <= 180;
  const inner = (abs: number) => (ref === null ? abs : ccw ? (abs - ref + 360) % 360 : (ref - abs + 360) % 360);
  const abs = (inner: number) => (ref === null ? inner : ccw ? ref + inner : ref - inner);
  const typedOnly = d.length === null && d.angle === null;
  if (typedOnly && d.snap) {
    const s = snapRoomPoint(plan, cursor.x, cursor.z, { ...d.snap, grid: null });
    if (s.kind) return { ...s, length: Math.hypot(s.x - start.x, s.z - start.z), angle: inner(visualAngle(s.x - start.x, s.z - start.z)) };
  }
  let ang = d.angle !== null ? d.angle : inner(curAngle);
  if (d.angle === null && d.angleStep) ang = Math.round(ang / d.angleStep) * d.angleStep;
  const dir = abs(ang);
  // Distance along the chosen direction: the cursor projected onto it.
  const r = (dir * Math.PI) / 180;
  const along = (cursor.x - start.x) * Math.cos(r) - (cursor.z - start.z) * Math.sin(r);
  let len = d.length !== null ? d.length : Math.max(0, along);
  if (d.length === null && d.lengthStep) len = Math.round(len / d.lengthStep) * d.lengthStep;
  // Neither typed nor increments: the grid (when on) places the end point.
  if (typedOnly && !d.lengthStep && !d.angleStep && d.snap?.grid) {
    const g = d.snap.grid;
    const x = Math.round(cursor.x / g) * g;
    const z = Math.round(cursor.z / g) * g;
    return { x, z, kind: 'grid', length: Math.hypot(x - start.x, z - start.z), angle: inner(visualAngle(x - start.x, z - start.z)) };
  }
  const p = polar(start.x, start.z, dir, len);
  return { ...p, kind: null, length: len, angle: ang };
}

// ---------- 3D ----------

export interface RoomWallBuild {
  lineId: string;
  /** Start point and unit direction of the inner face line. */
  ax: number;
  az: number;
  ux: number;
  uz: number;
  length: number;
  /** Unit normal pointing out of the room (the thickness goes this way). */
  nx: number;
  nz: number;
  thickness: number;
  /** The outer face runs past its ends by these amounts (negative = stops short), mitring the corner with the next wall; the inner face ends in the points. */
  extA: number;
  extB: number;
  /** Top edge height at u = 0 and u = length (corner heights); linear between and beyond. */
  hA: number;
  hB: number;
  /** Openings (windows, doors) cut through the wall, in wall coordinates (u along, y up). */
  holes: { u0: number; u1: number; y0: number; y1: number }[];
  /**
   * The ceiling plane over this wall, height = a·x + b·z + c (room coordinates, µm): the wall's top is
   * cut by it (like a boolean), so its top surface lies in the ceiling and a flat ceiling rests on it —
   * tilted across the thickness when the ceiling slopes across the wall.
   */
  ceil: { a: number; b: number; c: number };
  /** Ceiling planes of the walls joined at the start / end: the mitred corner is cut by both (no step). */
  ceilA?: { a: number; b: number; c: number };
  ceilB?: { a: number; b: number; c: number };
  /** Roof planes of the wall joined at the start / end: the corner block beyond the inner corner is cut by them too. */
  nbA?: { a: number; b: number; c: number }[];
  nbB?: { a: number; b: number; c: number }[];
}

/** Ceiling height over a room point for a wall's ceiling plane. */
export const ceilingAt = (w: Pick<RoomWallBuild, 'ceil'>, x: number, z: number): number => w.ceil.a * x + w.ceil.b * z + w.ceil.c;

/** Height of a wall's top edge at `u` (µm along the wall). */
export const wallTopAt = (w: Pick<RoomWallBuild, 'hA' | 'hB' | 'length'>, u: number): number => w.hA + ((w.hB - w.hA) * u) / (w.length || 1);

export interface RoomBuild {
  walls: RoomWallBuild[];
  items: { item: RoomItem; wall: RoomWallBuild; u0: number; u1: number }[];
  /** Floor outline when the walls close a loop. */
  floor: { x: number; z: number }[] | null;
  /** Ceiling of a closed room: triangles through the wall corners at their heights. */
  ceiling: { x: number; z: number; h: number }[][] | null;
  issues: Issue[];
}

type CP = { x: number; z: number; h: number };

/** Plane height = a·x + b·z + c through three points (null when they are on one line). */
function planeThrough(p: CP, q: CP, r: CP): { a: number; b: number; c: number } | null {
  const det = (q.x - p.x) * (r.z - p.z) - (r.x - p.x) * (q.z - p.z);
  if (Math.abs(det) < 1e-6) return null;
  const a = ((q.h - p.h) * (r.z - p.z) - (r.h - p.h) * (q.z - p.z)) / det;
  const b = ((r.h - p.h) * (q.x - p.x) - (q.h - p.h) * (r.x - p.x)) / det;
  return { a, b, c: p.h - a * p.x - b * p.z };
}

/** Ear-clipping triangulation of a simple polygon (indices). */
function triangulate(poly: { x: number; z: number }[]): [number, number, number][] {
  const n = poly.length;
  if (n < 3) return [];
  let area = 0;
  for (let i = 0; i < n; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % n]!;
    area += a.x * b.z - b.x * a.z;
  }
  const sgn = area > 0 ? 1 : -1;
  const idx = poly.map((_, i) => i);
  const out: [number, number, number][] = [];
  const cross = (o: { x: number; z: number }, a: { x: number; z: number }, b: { x: number; z: number }) => (a.x - o.x) * (b.z - o.z) - (a.z - o.z) * (b.x - o.x);
  const inside = (p: { x: number; z: number }, a: { x: number; z: number }, b: { x: number; z: number }, c: { x: number; z: number }) =>
    sgn * cross(a, b, p) > 0 && sgn * cross(b, c, p) > 0 && sgn * cross(c, a, p) > 0;
  let guard = 0;
  while (idx.length > 3 && guard++ < 10000) {
    let cut = false;
    for (let k = 0; k < idx.length; k++) {
      const i0 = idx[(k + idx.length - 1) % idx.length]!;
      const i1 = idx[k]!;
      const i2 = idx[(k + 1) % idx.length]!;
      const [a, b, c] = [poly[i0]!, poly[i1]!, poly[i2]!];
      if (sgn * cross(a, b, c) <= 0) continue;
      if (idx.some((j) => j !== i0 && j !== i1 && j !== i2 && inside(poly[j]!, a, b, c))) continue;
      out.push([i0, i1, i2]);
      idx.splice(k, 1);
      cut = true;
      break;
    }
    if (!cut) break;
  }
  if (idx.length === 3) out.push([idx[0]!, idx[1]!, idx[2]!]);
  return out;
}

/** A closed loop of walls (every point of it used by exactly two walls), as an ordered outline. */
export function roomOutline(plan: RoomPlan): RoomPoint[] | null {
  const walls = plan.lines.filter((l) => l.kind === 'wall');
  if (walls.length < 3) return null;
  const deg = new Map<string, RoomLine[]>();
  for (const w of walls) for (const k of [w.a, w.b]) deg.set(k, [...(deg.get(k) ?? []), w]);
  const start = walls[0]!;
  const out: RoomPoint[] = [roomPoint(plan, start.a)!];
  let cur = start;
  let at = start.b;
  const seen = new Set([start.id]);
  while (at !== start.a) {
    const next = (deg.get(at) ?? []).find((w) => !seen.has(w.id));
    if (!next || (deg.get(at) ?? []).length !== 2) return null;
    out.push(roomPoint(plan, at)!);
    seen.add(next.id);
    cur = next;
    at = cur.a === at ? cur.b : cur.a;
  }
  return out.length >= 3 ? out : null;
}

/** Points of an open plan that is a single chain of walls, in order (null for any other shape). */
function openWallChain(plan: RoomPlan): RoomPoint[] | null {
  const walls = plan.lines.filter((l) => l.kind === 'wall');
  if (walls.length < 2) return null;
  const deg = new Map<string, RoomLine[]>();
  for (const w of walls) for (const k of [w.a, w.b]) deg.set(k, [...(deg.get(k) ?? []), w]);
  const ends = [...deg].filter(([, ls]) => ls.length === 1).map(([k]) => k);
  if (ends.length !== 2 || [...deg.values()].some((ls) => ls.length > 2)) return null;
  const out: RoomPoint[] = [];
  const seen = new Set<string>();
  let at = ends[0]!;
  for (;;) {
    const p = roomPoint(plan, at);
    if (!p) return null;
    out.push(p);
    const next = (deg.get(at) ?? []).find((w) => !seen.has(w.id));
    if (!next) break;
    seen.add(next.id);
    at = next.a === at ? next.b : next.a;
  }
  return seen.size === walls.length && out.length >= 3 ? out : null;
}

function insidePolygon(poly: { x: number; z: number }[], x: number, z: number): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if (a.z > z !== b.z > z && x < ((b.x - a.x) * (z - a.z)) / (b.z - a.z) + a.x) inside = !inside;
  }
  return inside;
}

export function buildRoom(plan: RoomPlan): RoomBuild {
  const issues: Issue[] = [];
  const H = plan.wallHeight ?? 0;
  const t = plan.wallThickness;
  const walls = plan.lines.filter((l) => l.kind === 'wall');
  const pts = walls.flatMap((w) => [roomPoint(plan, w.a), roomPoint(plan, w.b)]).filter((p): p is RoomPoint => !!p);
  const outline = roomOutline(plan);
  // An open plan that is one chain of walls (an L, a U …) is closed in thought by its two free ends, so
  // the room is on the inner side of that shape; otherwise: away from the centre of all walls.
  const chain = outline ? null : openWallChain(plan);
  const cx = pts.length ? pts.reduce((s, p) => s + p.x, 0) / pts.length : 0;
  const cz = pts.length ? pts.reduce((s, p) => s + p.z, 0) / pts.length : 0;
  const out: RoomWallBuild[] = [];
  const items: RoomBuild['items'] = [];
  // Pass 1: the outward normal of every wall (the thickness goes that way).
  const sides = new Map<string, { g: NonNullable<ReturnType<typeof lineGeom>>; nx: number; nz: number }>();
  for (const w of walls) {
    const g = lineGeom(plan, w);
    if (!g || g.length <= 0) continue;
    // Left normal of a → b is (uz, -ux) in x/z.
    let nx = g.uz;
    let nz = -g.ux;
    const mx = (g.a.x + g.b.x) / 2;
    const mz = (g.a.z + g.b.z) / 2;
    const poly = outline ?? chain;
    const flip = poly ? insidePolygon(poly, mx + nx * 1000, mz + nz * 1000) : (cx - mx) * nx + (cz - mz) * nz > 0;
    if (flip) [nx, nz] = [-nx, -nz];
    // Turned by hand ("Okreni zid"): the thickness goes to the other side.
    if (w.flip) [nx, nz] = [-nx, -nz];
    sides.set(w.id, { g, nx, nz });
  }
  // Corner of two walls: the line is the inner face, so the inner faces meet in the shared point; the
  // outer faces meet where their offset lines cross (a mitre — no overlap, no gap). Walls whose
  // thickness goes to different sides of the corner, collinear walls and points with 3+ walls simply butt.
  const outerExt = (w: RoomLine, at: string): number => {
    const others = walls.filter((o) => o !== w && sides.has(o.id) && (o.a === at || o.b === at));
    const me = sides.get(w.id);
    if (others.length !== 1 || !me) return 0;
    const o = others[0]!;
    const os = sides.get(o.id)!;
    const dW = at === w.a ? { x: me.g.ux, z: me.g.uz } : { x: -me.g.ux, z: -me.g.uz };
    const dO = at === o.a ? { x: os.g.ux, z: os.g.uz } : { x: -os.g.ux, z: -os.g.uz };
    const det = -dW.x * dO.z + dO.x * dW.z;
    if (Math.abs(det) < 1e-6) return 0;
    const ex = (os.nx - me.nx) * t;
    const ez = (os.nz - me.nz) * t;
    const sW = (-ex * dO.z + dO.x * ez) / det;
    const sO = (dW.x * ez - dW.z * ex) / det;
    const same = (sW <= 1 && sO <= 1) || (sW >= -1 && sO >= -1);
    if (!same || Math.abs(sW) > 20 * t) return 0;
    return -sW;
  };
  // Ceiling: one plane when all corners are on one (e.g. a single roof slope), otherwise the triangles
  // of the floor outline at the corner heights. Each wall takes the plane over it.
  const ceilOf = new Map<string, { a: number; b: number; c: number }>();
  let ceilingTris: CP[][] | null = null;
  // The ceiling covers the closed outline, or an open chain of walls closed in thought between its ends.
  const ceilPoly = outline ?? chain;
  if (ceilPoly && H) {
    const cps: CP[] = ceilPoly.map((p) => ({ x: p.x, z: p.z, h: p.h ?? H }));
    const tris = triangulate(cps).map((t) => t.map((i) => cps[i]!) as [CP, CP, CP]);
    ceilingTris = tris;
    const flat = tris.length ? planeThrough(...tris[0]!) : null;
    const single = flat && cps.every((p) => Math.abs(flat.a * p.x + flat.b * p.z + flat.c - p.h) < 500) ? flat : null;
    for (const w of walls) {
      const a = roomPoint(plan, w.a);
      const b = roomPoint(plan, w.b);
      if (!a || !b) continue;
      if (single) {
        ceilOf.set(w.id, single);
        continue;
      }
      const ia = ceilPoly.findIndex((p) => p.id === a.id);
      const ib = ceilPoly.findIndex((p) => p.id === b.id);
      const tri = triangulate(cps).find((t) => t.includes(ia) && t.includes(ib));
      const pl = tri ? planeThrough(cps[tri[0]]!, cps[tri[1]]!, cps[tri[2]]!) : null;
      if (pl) ceilOf.set(w.id, pl);
    }
  }
  for (const w of walls) {
    const side = sides.get(w.id);
    if (!side) continue;
    const { g, nx, nz } = side;
    // Outer face runs on (or stops short) at joined ends; the inner face always ends in the point.
    const extA = Math.round(outerExt(w, w.a));
    const extB = Math.round(outerExt(w, w.b));
    const hA = g.a.h ?? H;
    const hB = g.b.h ?? H;
    // Open plans / no plane: level across the thickness, sloping along the wall (through both corners).
    const along = (hB - hA) / (g.length || 1);
    const ceil = ceilOf.get(w.id) ?? { a: along * g.ux, b: along * g.uz, c: hA - along * (g.ux * g.a.x + g.uz * g.a.z) };
    const wb: RoomWallBuild = { lineId: w.id, ax: g.a.x, az: g.a.z, ux: g.ux, uz: g.uz, length: g.length, nx, nz, thickness: t, extA, extB, hA, hB, holes: [], ceil };
    const mine = plan.items.filter((i) => i.lineId === w.id);
    const holes: { u0: number; u1: number; y0: number; y1: number }[] = [];
    for (const it of mine) {
      const s = itemSpan(plan, it)!;
      const name = ROOM_ITEM_LABEL[it.kind];
      if (s.u0 < -SAME_POINT || s.u1 > g.length + SAME_POINT) issues.push({ severity: 'warning', code: 'room.item-outside', message: `${name} (${it.id}) izlazi van zida.`, ref: it.id });
      if (H && it.elevation + it.height > Math.min(wallTopAt(wb, Math.max(0, s.u0)), wallTopAt(wb, Math.min(g.length, s.u1))) + SAME_POINT) issues.push({ severity: 'warning', code: 'room.item-above', message: `${name} (${it.id}) je viši od zida.`, ref: it.id });
      items.push({ item: it, wall: wb, u0: s.u0, u1: s.u1 });
      if (ROOM_OPENINGS.includes(it.kind)) {
        const u0 = Math.max(0, s.u0);
        const u1 = Math.min(g.length, s.u1);
        const top = Math.min(wallTopAt(wb, u0), wallTopAt(wb, u1)) - 1000;
        const y1 = Math.min(top, it.elevation + it.height);
        if (u1 - u0 > SAME_POINT && y1 - it.elevation > SAME_POINT) holes.push({ u0, u1, y0: Math.max(0, it.elevation), y1 });
      }
    }
    for (let i = 0; i < holes.length; i++)
      for (let j = i + 1; j < holes.length; j++) {
        const a = holes[i]!;
        const b = holes[j]!;
        if (Math.min(a.u1, b.u1) - Math.max(a.u0, b.u0) > SAME_POINT && Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0) > SAME_POINT) issues.push({ severity: 'warning', code: 'room.items-overlap', message: 'Dva otvora na istom zidu se preklapaju.' });
      }
    wb.holes = holes;
    out.push(wb);
  }
  // The roof over each wall end: the plane through the wall's top line and the joined wall's top line
  // (two lines meeting in the corner). The wall is cut by these planes like a boolean, so a sloping
  // roof cuts the low wall across its thickness and the gable walls along their length — open plans too.
  for (const wb of out) {
    const line = walls.find((l) => l.id === wb.lineId)!;
    const pa = roomPoint(plan, line.a)!;
    const pb = roomPoint(plan, line.b)!;
    const A: CP = { x: pa.x, z: pa.z, h: pa.h ?? H };
    const B: CP = { x: pb.x, z: pb.z, h: pb.h ?? H };
    for (const [end, pid] of [['ceilA', line.a], ['ceilB', line.b]] as const) {
      const others = walls.filter((o) => o.id !== line.id && (o.a === pid || o.b === pid));
      if (others.length !== 1) continue;
      const o = others[0]!;
      const far = roomPoint(plan, o.a === pid ? o.b : o.a);
      if (!far) continue;
      const pl = planeThrough(A, B, { x: far.x, z: far.z, h: far.h ?? H });
      if (pl) wb[end] = pl;
    }
  }
  for (const wb of out) {
    const line = walls.find((l) => l.id === wb.lineId)!;
    for (const [end, pid] of [['nbA', line.a], ['nbB', line.b]] as const) {
      const others = walls.filter((o) => o.id !== line.id && (o.a === pid || o.b === pid));
      const o = others.length === 1 ? out.find((x) => x.lineId === others[0]!.id) : undefined;
      const planes = o ? [o.ceilA, o.ceilB].filter((c): c is NonNullable<typeof c> => !!c) : [];
      if (planes.length) wb[end] = planes;
    }
  }
  // The floor too: the closed outline, or an open chain closed between its ends.
  return { walls: out, items, floor: ceilPoly ? ceilPoly.map((p) => ({ x: p.x, z: p.z })) : null, ceiling: ceilingTris, issues };
}
